/**
 * Curated sample-analyses index.
 *
 * Each entry describes one bundled dataset under public/samples/.
 * `id` is the URL slug and the CSV file stem. `preview` carries a
 * tiny pre-rendered analysis result (a frequencies table, a small
 * crosstab, or a tiny sparkline series) so the detail page can
 * show "what Sparrow does with this file" without actually running
 * the WASM engine — the real Sparrow lives at sparrow.dasc.nl and
 * we link out for the live run.
 *
 * Numbers in `preview` are real values computed off the generated
 * CSVs (see scripts/build-samples.mjs) — re-run that script and
 * update these if you bump row counts or distributions.
 */

export type SampleCategory =
  | "Classification"
  | "Survey"
  | "Time series"
  | "Comparison"
  | "Health"
  | "Conversion"

export interface SamplePreview {
  /** Heading shown above the small example table/chart. */
  title: string
  /** "Frequencies", "Crosstab", "Descriptives", "Sparkline" — kind
   *  of analysis Sparrow would run. */
  kind: "frequencies" | "crosstab" | "descriptives" | "sparkline" | "anova"
  /** Pre-rendered table cells. For sparkline previews, leave this
   *  empty and put the numeric series in `series` instead. */
  table?: {
    headers: string[]
    rows: (string | number)[][]
    /** Optional trailing "total" row, rendered with emphasis. */
    total?: (string | number)[]
  }
  /** Used by sparkline previews. */
  series?: { label: string; values: number[] }[]
  /** One-line caption under the preview. */
  caption?: string
}

export interface SampleVariable {
  name: string
  type: "nominal" | "ordinal" | "scale" | "date" | "string"
  description?: string
}

export interface Sample {
  id: string
  title: string
  blurb: string
  category: SampleCategory
  rows: number
  cols: number
  fileFormat: "csv"
  source: string
  attribution: string
  /** Procedure name(s) Sparrow's UI would call this — drives the
   *  "canonical analysis" line on the gallery card. */
  canonical: string
  variables: SampleVariable[]
  preview: SamplePreview
}

export const SAMPLES: Sample[] = [
  {
    id: "titanic",
    title: "Titanic passengers",
    blurb:
      "Survival outcome for 891 passengers of the RMS Titanic. The classic teaching dataset for binary classification — survival rate splits sharply by sex and passenger class.",
    category: "Classification",
    rows: 891,
    cols: 10,
    fileFormat: "csv",
    source: "public/samples/titanic.csv",
    attribution:
      "Synthetic re-creation of the Kaggle Titanic dataset. Distribution tuned so descriptive statistics land near the historical figures.",
    canonical: "Descriptives + crosstab (survival × sex × class)",
    variables: [
      { name: "passenger_id", type: "scale" },
      { name: "survived",     type: "nominal", description: "0 = No, 1 = Yes" },
      { name: "pclass",       type: "ordinal", description: "1st / 2nd / 3rd class" },
      { name: "name",         type: "string" },
      { name: "sex",          type: "nominal" },
      { name: "age",          type: "scale" },
      { name: "sib_sp",       type: "scale", description: "Siblings + spouses aboard" },
      { name: "parch",        type: "scale", description: "Parents + children aboard" },
      { name: "fare",         type: "scale" },
      { name: "embarked",     type: "nominal", description: "S = Southampton, C = Cherbourg, Q = Queenstown" },
    ],
    preview: {
      title: "Survival × Sex",
      kind: "crosstab",
      caption: "Crosstab of survival outcome by passenger sex.",
      table: {
        headers: ["sex", "survived = 0", "survived = 1", "% survived"],
        rows: [
          ["female", 81, 234, "74.3%"],
          ["male", 468, 108, "18.8%"],
        ],
        total: ["total", 549, 342, "38.4%"],
      },
    },
  },
  {
    id: "iris",
    title: "Iris flowers",
    blurb:
      "Fisher's 1936 iris dataset — 150 flowers, 4 measurements, 3 species. Pristine for between-group comparisons and the canonical first example for any new analytics user.",
    category: "Comparison",
    rows: 150,
    cols: 5,
    fileFormat: "csv",
    source: "public/samples/iris.csv",
    attribution: "Fisher (1936), public domain.",
    canonical: "One-way ANOVA on petal length across species",
    variables: [
      { name: "sepal_length", type: "scale", description: "cm" },
      { name: "sepal_width",  type: "scale", description: "cm" },
      { name: "petal_length", type: "scale", description: "cm" },
      { name: "petal_width",  type: "scale", description: "cm" },
      { name: "species",      type: "nominal" },
    ],
    preview: {
      title: "Petal length by species",
      kind: "descriptives",
      caption: "Mean and SD of petal length (cm), grouped by species.",
      table: {
        headers: ["species", "n", "mean", "sd", "min", "max"],
        rows: [
          ["setosa",     50, 1.46, 0.17, 1.0, 1.9],
          ["versicolor", 50, 4.26, 0.47, 3.0, 5.1],
          ["virginica",  50, 5.55, 0.55, 4.5, 6.9],
        ],
        total: ["all", 150, 3.76, 1.77, 1.0, 6.9],
      },
    },
  },
  {
    id: "nhanes-lite",
    title: "NHANES body measurements",
    blurb:
      "A 1,000-row slice of adult body measurements — height, weight, BMI, blood pressure, cholesterol. Made for descriptives, sex-stratified crosstabs, and quick health regressions.",
    category: "Health",
    rows: 1000,
    cols: 9,
    fileFormat: "csv",
    source: "public/samples/nhanes-lite.csv",
    attribution:
      "Synthetic, modelled on NHANES adult subset distributions. Use as if it were real for teaching; do not cite as primary data.",
    canonical: "Descriptives + sex-stratified comparisons",
    variables: [
      { name: "id",          type: "scale" },
      { name: "sex",         type: "nominal" },
      { name: "age",         type: "scale" },
      { name: "height_cm",   type: "scale" },
      { name: "weight_kg",   type: "scale" },
      { name: "bmi",         type: "scale" },
      { name: "sys_bp",      type: "scale", description: "Systolic blood pressure (mmHg)" },
      { name: "dia_bp",      type: "scale", description: "Diastolic blood pressure (mmHg)" },
      { name: "cholesterol", type: "scale", description: "Total serum cholesterol (mg/dL)" },
    ],
    preview: {
      title: "BMI by sex",
      kind: "descriptives",
      caption: "Body-mass-index summary, split by sex.",
      table: {
        headers: ["sex", "n", "mean BMI", "sd", "% obese (≥30)"],
        rows: [
          ["female", 503, 27.4, 5.8, "32.4%"],
          ["male",   497, 28.1, 5.6, "35.8%"],
        ],
        total: ["all", 1000, 27.7, 5.7, "34.1%"],
      },
    },
  },
  {
    id: "employee-engagement",
    title: "Employee engagement survey",
    blurb:
      "Five-hundred responses to a six-question engagement pulse on a 1–5 Likert scale. Built for frequencies, item-level descriptives, and a quick factor reduction.",
    category: "Survey",
    rows: 500,
    cols: 9,
    fileFormat: "csv",
    source: "public/samples/employee-engagement.csv",
    attribution:
      "Synthetic. Department-level means shifted so a between-group comparison surfaces a recognisable signal.",
    canonical: "Frequencies on each Q item + departmental means",
    variables: [
      { name: "employee_id",    type: "scale" },
      { name: "department",     type: "nominal" },
      { name: "tenure_years",   type: "scale" },
      { name: "q1_purpose",     type: "ordinal", description: "1 = strongly disagree, 5 = strongly agree" },
      { name: "q2_recognition", type: "ordinal" },
      { name: "q3_growth",      type: "ordinal" },
      { name: "q4_manager",     type: "ordinal" },
      { name: "q5_workload",    type: "ordinal" },
      { name: "q6_recommend",   type: "ordinal", description: "Would you recommend us as a place to work?" },
    ],
    preview: {
      title: "Q1 \"I find my work meaningful\" — frequencies",
      kind: "frequencies",
      caption: "Distribution of responses on a 1–5 Likert scale.",
      table: {
        headers: ["response", "n", "%", "cumulative %"],
        rows: [
          ["1 — strongly disagree", 22,  "4.4%", "4.4%"],
          ["2",                      53, "10.6%", "15.0%"],
          ["3",                     142, "28.4%", "43.4%"],
          ["4",                     188, "37.6%", "81.0%"],
          ["5 — strongly agree",     95, "19.0%", "100.0%"],
        ],
        total: ["total", 500, "100%", "—"],
      },
    },
  },
  {
    id: "ab-test",
    title: "A/B test conversions",
    blurb:
      "Two-arm conversion experiment on 2,000 sessions. Arm B is the winner — chi-square on the 2 × 2 table makes the lift explicit.",
    category: "Conversion",
    rows: 2000,
    cols: 7,
    fileFormat: "csv",
    source: "public/samples/ab-test.csv",
    attribution: "Synthetic — designed so arm B wins by ~2.9 percentage points.",
    canonical: "Chi-square on arm × converted, plus revenue summary",
    variables: [
      { name: "user_id",         type: "scale" },
      { name: "arm",             type: "nominal", description: "A = control, B = variant" },
      { name: "device",          type: "nominal" },
      { name: "country",         type: "nominal" },
      { name: "session_seconds", type: "scale" },
      { name: "converted",       type: "nominal", description: "0 = no, 1 = yes" },
      { name: "revenue_eur",     type: "scale", description: "0 when converted = 0" },
    ],
    preview: {
      title: "Arm × converted",
      kind: "crosstab",
      caption: "Conversion rate by experiment arm.",
      table: {
        headers: ["arm", "not converted", "converted", "rate"],
        rows: [
          ["A (control)", 925, 79, "7.9%"],
          ["B (variant)", 882, 114, "11.4%"],
        ],
        total: ["total", 1807, 193, "9.7%"],
      },
    },
  },
  {
    id: "covid-daily",
    title: "Daily COVID cases (NL, 90 days)",
    blurb:
      "A 90-day window through one infection wave in the Netherlands. Build for rolling means, weekly seasonality decomposition, and trend visualisation.",
    category: "Time series",
    rows: 90,
    cols: 6,
    fileFormat: "csv",
    source: "public/samples/covid-daily.csv",
    attribution: "Synthetic wave shape modelled after real surveillance data.",
    canonical: "7-day rolling mean of new_cases + sparkline",
    variables: [
      { name: "date",            type: "date" },
      { name: "country",         type: "nominal" },
      { name: "new_cases",       type: "scale" },
      { name: "new_deaths",      type: "scale" },
      { name: "tests",           type: "scale" },
      { name: "positivity_pct",  type: "scale" },
    ],
    preview: {
      title: "new_cases — 90-day sparkline",
      kind: "sparkline",
      caption: "Daily cases. A single wave, peak around day 40.",
      // Twelve evenly spaced points, scaled to 0..100 so the sparkline
      // renderer can use them without further normalisation.
      series: [{
        label: "new_cases",
        values: [12, 22, 38, 56, 74, 88, 95, 88, 70, 48, 28, 14],
      }],
    },
  },
  {
    id: "student-scores",
    title: "Student test scores",
    blurb:
      "Math and reading scores for 600 students across four schools and four high-school grades. Made for school-level ANOVA and subgroup comparisons.",
    category: "Comparison",
    rows: 600,
    cols: 6,
    fileFormat: "csv",
    source: "public/samples/student-scores.csv",
    attribution: "Synthetic — school baselines tuned for a visible between-school effect.",
    canonical: "One-way ANOVA on math_score across schools",
    variables: [
      { name: "student_id",    type: "scale" },
      { name: "school",        type: "nominal" },
      { name: "grade",         type: "ordinal", description: "9 / 10 / 11 / 12" },
      { name: "sex",           type: "nominal" },
      { name: "math_score",    type: "scale" },
      { name: "reading_score", type: "scale" },
    ],
    preview: {
      title: "Math score by school",
      kind: "anova",
      caption: "One-way ANOVA — math_score ~ school.",
      table: {
        headers: ["school", "n", "mean", "sd"],
        rows: [
          ["Argonaut HS",   150, 72.1, 10.9],
          ["Beech Academy", 150, 64.0, 10.7],
          ["Cedar Prep",    150, 77.8, 11.1],
          ["Delta College", 150, 59.7, 11.2],
        ],
        total: ["all", 600, 68.4, 12.6],
      },
    },
  },
]

/** Group samples by category for the gallery — keeps a stable order. */
export const CATEGORIES: SampleCategory[] = [
  "Classification",
  "Comparison",
  "Survey",
  "Conversion",
  "Health",
  "Time series",
]

export function findSample(id: string | undefined): Sample | undefined {
  if (!id) return undefined
  return SAMPLES.find(s => s.id === id)
}
